import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Heart, Loader2, Save, ChevronDown, ChevronRight, Lock } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "submitted", label: "Submitted", color: "#6b7280" },
  { value: "under_review", label: "Under review", color: "#0B1F4D" },
  { value: "approved", label: "Approved", color: "#15803d" },
  { value: "declined", label: "Declined", color: "#991b1b" },
  { value: "paid", label: "Paid", color: "#8A2D3B" },
];

/**
 * Karuṇā Fund admin queue — confidential hardship grant applications.
 */
export default function KarunaFundManager() {
  const [items, setItems] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [open, setOpen] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/karuna-fund");
      setItems(r.data.items || []);
      setByStatus(r.data.by_status || {});
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };

  const save = async (id) => {
    const body = drafts[id];
    if (!body || !body.status) {
      toast.error("Pick a status first.");
      return;
    }
    setBusy(id);
    try {
      await api.put(`/admin/karuna-fund/${id}`, body);
      toast.success("Application updated");
      setDrafts((d) => { const next = { ...d }; delete next[id]; return next; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="karuna-fund-manager">
      <div>
        <h3 className="font-display text-xl text-navy flex items-center gap-2">
          <Heart size={18} style={{ color: "#8A2D3B" }} /> Karuṇā Fund — confidential applications
        </h3>
        <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
          <Lock size={11} /> Only trustees who handle the Karuṇā Fund should review these. Notes are auditable.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {STATUS_OPTIONS.map((s) => (
          <div key={s.value} className="bg-white border border-navy/10 rounded-sm p-3" data-testid={`kf-stat-${s.value}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.label}</div>
            <div className="font-display text-2xl mt-1" style={{ color: s.color }}>
              {byStatus[s.value] || 0}
            </div>
          </div>
        ))}
      </div>

      {loading && <p className="italic text-slate-500">Loading…</p>}

      <div className="space-y-2">
        {items.map((it) => {
          const isOpen = !!open[it.application_id];
          const draft = drafts[it.application_id] || {};
          const status = draft.status || it.status;
          const note = draft.trustee_note ?? (it.trustee_note || "");
          const amount = draft.amount_awarded_gbp ?? (it.amount_awarded_gbp ?? "");
          const dirty = Object.keys(draft).length > 0;
          return (
            <div key={it.application_id} className="bg-white border border-navy/10 rounded-sm">
              <button
                onClick={() => setOpen((o) => ({ ...o, [it.application_id]: !isOpen }))}
                data-testid={`kf-toggle-${it.application_id}`}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-pearl/40"
              >
                <div>
                  <div className="text-sm font-medium text-navy">
                    {it.full_name} <span className="text-slate-400">·</span>{" "}
                    <span className="text-xs text-slate-500">{it.nature}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {it.application_id} · {new Date(it.created_at).toLocaleDateString("en-GB")} ·
                    requested £{it.amount_requested_gbp ?? "—"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm"
                    style={{
                      backgroundColor: `${STATUS_OPTIONS.find((s) => s.value === it.status)?.color || "#888"}15`,
                      color: STATUS_OPTIONS.find((s) => s.value === it.status)?.color || "#888",
                    }}
                  >
                    {STATUS_OPTIONS.find((s) => s.value === it.status)?.label || it.status}
                  </span>
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-navy/10 space-y-3 pt-3" data-testid={`kf-detail-${it.application_id}`}>
                  <Field label="Email">{it.email}</Field>
                  {it.phone && <Field label="Phone">{it.phone}</Field>}
                  {it.location && <Field label="Location">{it.location}</Field>}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Description</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap bg-pearl/50 p-3 rounded-sm border border-navy/5">
                      {it.description}
                    </p>
                  </div>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-navy/10">
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Status</span>
                      <select
                        value={status}
                        onChange={(e) => setDrafts((d) => ({
                          ...d, [it.application_id]: { ...d[it.application_id], status: e.target.value },
                        }))}
                        data-testid={`kf-status-${it.application_id}`}
                        className="w-full border border-navy/15 rounded-sm px-2 py-2 text-sm"
                      >
                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Amount awarded (£)</span>
                      <input
                        type="number" step="50"
                        value={amount}
                        onChange={(e) => setDrafts((d) => ({
                          ...d, [it.application_id]: { ...d[it.application_id], amount_awarded_gbp: e.target.value === "" ? null : parseFloat(e.target.value) },
                        }))}
                        data-testid={`kf-amount-${it.application_id}`}
                        className="w-full border border-navy/15 rounded-sm px-2 py-2 text-sm"
                      />
                    </label>
                    <div />
                    <label className="block sm:col-span-3">
                      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Trustee note (private)</span>
                      <textarea
                        value={note}
                        onChange={(e) => setDrafts((d) => ({
                          ...d, [it.application_id]: { ...d[it.application_id], trustee_note: e.target.value },
                        }))}
                        data-testid={`kf-note-${it.application_id}`}
                        rows={2}
                        className="w-full border border-navy/15 rounded-sm px-2 py-2 text-sm"
                      />
                    </label>
                  </div>
                  <button
                    disabled={!dirty || busy === it.application_id}
                    onClick={() => save(it.application_id)}
                    data-testid={`kf-save-${it.application_id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-pearl rounded-sm text-xs font-medium disabled:opacity-40 hover:opacity-90"
                    style={{ backgroundColor: "#8A2D3B" }}
                  >
                    {busy === it.application_id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Save review
                  </button>
                  {it.reviewed_by && (
                    <p className="text-[10px] text-slate-400">Last reviewed by {it.reviewed_by} · {new Date(it.updated_at).toLocaleString("en-GB")}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!loading && items.length === 0 && (
          <p className="italic text-slate-500 py-6">No Karuṇā Fund applications yet.</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm text-navy">{children}</div>
    </div>
  );
}
